import { AnimatePresence, motion } from "motion/react";
import { useLayoutEffect, useRef, useState } from "react";
import { Avatar, Segmented } from "../../../components/ui/primitives";
import { DEPTS, EDITORS, PEOPLE, type Dept, type PersonId } from "../../../data/people";
import { dayTaskCount, isPastDay, tasksLabel } from "../../../lib/domain";
import { easeOut } from "../../../lib/motion";
import { dayPlan, VIEW_END, VIEW_START } from "../../../lib/schedule";
import { dayInfo, dayOf, DAYS } from "../../../lib/time";
import { cn } from "../../../lib/utils";
import { HourGutter, TimeColumn, type CalendarProps } from "./shared";

/** Календарь команды: день как в Google Календаре, колонки — монтажёры выбранного отдела */
export function DayByPeople({
  now,
  tasks,
  busy,
  pick,
  onTaskClick,
  onAdd,
  compact,
  editors = EDITORS,
  dept,
  onDept,
  projects,
  projectId,
  onProject,
}: CalendarProps & {
  editors?: PersonId[];
  /** отдел и переключатель отделов — у каждого отдела свои монтажёры (их заводят на платформе через роли) */
  dept?: Dept;
  onDept?: (d: Dept) => void;
  /** направления: колонки — монтажёры выбранного направления */
  projects?: { id: string; name: string }[];
  projectId?: string | null;
  onProject?: (id: string) => void;
}) {
  const today = dayOf(now);
  const [ownDay, setOwnDay] = useState(today);
  const day = pick ? pick.day : ownDay;
  const setDay = (d: number) => (pick ? pick.onPick(pick.editor, d) : setOwnDay(d));
  const list = pick?.excludeTaskId ? tasks.filter((t) => t.id !== pick.excludeTaskId) : tasks;
  // Если у кого-то задачи не влезают до 19:00, продлеваем сетку всем, чтобы до них можно было докрутить
  const viewEnd = Math.min(
    24,
    Math.max(
      VIEW_END,
      ...editors.map((e) => {
        const plan = dayPlan(e, day, list, busy, {
          draft: pick && pick.editor === e && pick.day === day ? { editor: e, day, title: pick.title, estimate: pick.estimate } : undefined,
        });
        return Math.ceil(Math.max(0, ...plan.slots.map((s) => s.segs[s.segs.length - 1][1])));
      }),
    ),
  );

  // Весь день помещается в доступную высоту: верх всегда виден, ничего не обрезается и не нужно крутить
  const area = useRef<HTMLDivElement>(null);
  const [avail, setAvail] = useState(0);
  useLayoutEffect(() => {
    const el = area.current;
    if (!el) return;
    const measure = () => setAvail(el.clientHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const headerH = compact ? 40 : 44;
  // снизу оставляем место под тень выбранной колонки, чтобы она не обрезалась
  const reserve = compact ? 22 : 6;
  const fallback = compact ? 40 : 50;
  const hourPx = avail ? Math.max(20, Math.min(compact ? 46 : 58, Math.floor((avail - headerH - reserve) / (viewEnd - VIEW_START)))) : fallback;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 items-center gap-3 pb-3">
        {/* Отдел один — переключать нечего: кнопка «B2G» лишь сбрасывала
            выбранное направление (08.10.2026). */}
        {dept && onDept && DEPTS.length > 1 && (
          <Segmented id={compact ? "dept-pick" : "dept-cal"} size="sm" value={dept} onChange={onDept} options={DEPTS.map((d) => ({ value: d, label: d }))} />
        )}
        {projects && projects.length > 1 && projectId && onProject && (
          <Segmented id={compact ? "proj-pick" : "proj-cal"} size="sm" value={projectId} onChange={onProject} options={projects.map((p) => ({ value: p.id, label: p.name }))} />
        )}
        <span className="flex-1" />
        {/* В окне постановки день выбирают в «Дедлайне» календарём (09.10.2026):
            вторая лента дней дублировала его и путала. Здесь — только чей день. */}
        {pick ? (
          <span className="text-[13px] text-muted-foreground">Занятость на {dayInfo(day).abbr.toLowerCase()}, {dayInfo(day).date} {dayInfo(day).month}</span>
        ) : (
        <div className="flex items-center gap-1">
          {DAYS.map((d) => {
            const past = isPastDay(d.idx, now);
            const disabled = !!pick && past;
            return (
              <button
                key={d.idx}
                type="button"
                disabled={disabled}
                onClick={() => setDay(d.idx)}
                className={cn(
                  "flex h-8 items-center gap-1.5 rounded-full px-3 text-[13px] font-medium transition-colors",
                  d.idx === day ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  past && d.idx !== day && "opacity-50",
                  disabled && "pointer-events-none opacity-30",
                )}
              >
                {d.abbr} {d.date}
                {d.idx === today && <span className={cn("text-xs", d.idx === day ? "text-ring" : "text-muted-foreground")}>сегодня</span>}
              </button>
            );
          })}
        </div>
        )}
      </div>

      <div ref={area} className="min-h-0 flex-1">
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={projectId ?? dept ?? "all"}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.18, ease: easeOut }}
          className="h-full"
        >
          {editors.length === 0 ? (
            <div className="grid h-full min-h-60 place-items-center rounded-[24px] bg-muted/50 text-sm text-muted-foreground">
              {projectId ? "В направлении пока нет монтажёров — добавьте их в «Отделение → Направления»" : `В отделе «${dept}» пока нет монтажёров`}
            </div>
          ) : (
            <div className="grid gap-x-2" style={{ gridTemplateColumns: `52px repeat(${editors.length}, minmax(0, 1fr))` }}>
              <div />
              {editors.map((e) => {
                const count = dayTaskCount(e, day, list, busy) + (pick && pick.editor === e && pick.day === day ? (pick.size ?? 1) : 0);
                return (
                  <div key={e} className="flex items-center gap-2.5 px-1 pb-2.5">
                    <Avatar id={e} size={compact ? 28 : 32} />
                    <span className="min-w-0 truncate text-sm font-semibold">{PEOPLE[e].name}</span>
                    <span className="ml-auto shrink-0 text-xs font-medium text-muted-foreground">{tasksLabel(count)}</span>
                  </div>
                );
              })}
              <HourGutter hourPx={hourPx} viewEnd={viewEnd} />
              {editors.map((e) => (
                <TimeColumn key={e} editor={e} day={day} now={now} tasks={tasks} busy={busy} hourPx={hourPx} viewEnd={viewEnd} pick={pick} onTaskClick={onTaskClick} onAdd={onAdd} />
              ))}
            </div>
          )}
        </motion.div>
      </AnimatePresence>
      </div>
    </div>
  );
}
